// src/services/websocket.js
import { ENV_CONFIG, API_ENDPOINTS } from '../config';

const MAX_RECONNECT_ATTEMPTS = 5;

// 認可エラーとみなすWebSocket closeコード
const AUTH_ERROR_CODES = [4401, 4403];

class WebSocketService {
  constructor() {
    this.socket = null;
    this.reconnectAttempts = 0;
    this.reconnectTimeout = null;
    this.messageHandlers = [];
    this.statusHandlers = [];
    this.authErrorHandlers = [];
    this.isDebugMode = ENV_CONFIG.DEBUG;
    this.getLatestToken = null; // 最新トークン取得用コールバック
  }

  /**
   * 最新のIDトークンを取得するためのコールバック関数を設定する。
   * リトライ時にトークンを最新化するために使用。
   * @param {() => string|null} tokenGetter - 最新のIDトークンを返す関数
   */
  setTokenGetter(tokenGetter) {
    this.getLatestToken = tokenGetter;
  }

  connect(idToken) {
    return new Promise((resolve, reject) => {
      if (!idToken) {
        reject(new Error('IDトークンが提供されていません'));
        return;
      }

      const socketUrl = `${API_ENDPOINTS.WEBSOCKET}`;
      // ?idToken=${encodeURIComponent(idToken)}`;

      if (this.isDebugMode) {
        console.log('デバッグモード WebSocket接続開始:', {
          url: API_ENDPOINTS.WEBSOCKET,
          tokenPreview: idToken.substring(0, 20) + '...'
        });
      }

      this.socket = new WebSocket(socketUrl, ["line-token", encodeURIComponent(idToken)]);

      this.socket.onopen = (event) => {
        this.reconnectAttempts = 0;
        this.notifyStatusChange(true);

        if (this.isDebugMode) {
          console.log('デバッグモード WebSocket接続成功');
        }

        resolve(this.socket);
      };

      this.socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (this.isDebugMode) {
            console.log('デバッグモード WebSocketメッセージ受信:', data);
          }

          this.notifyMessageHandlers(data);
        } catch (e) {
          console.error('WebSocketメッセージの解析エラー:', e);
        }
      };

      this.socket.onclose = (event) => {
        this.notifyStatusChange(false);
        this.socket = null;

        if (this.isDebugMode) {
          console.log('デバッグモード WebSocket切断:', {
            code: event.code,
            reason: event.reason,
            wasClean: event.wasClean
          });
        }

        // 認可エラーの場合はリトライせず、認証エラーハンドラに通知
        if (AUTH_ERROR_CODES.includes(event.code)) {
          console.warn('WebSocket認可エラーのため再接続を中止:', event.code);
          this.notifyAuthErrorHandlers(event.code, event.reason);
          return;
        }

        if (event.code !== 1000 && this.reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
          this.scheduleReconnect();
        }
      };

      this.socket.onerror = (error) => {
        this.notifyStatusChange(false);

        if (this.isDebugMode) {
          console.error('デバッグモード WebSocketエラー:', error);
        }

        reject(error);
      };

      setTimeout(() => {
        if (this.socket && this.socket.readyState === WebSocket.CONNECTING) {
          this.socket.close();
          reject(new Error("WebSocket接続タイムアウト"));
        }
      }, 10000);
    });
  }

  scheduleReconnect() {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }

    const baseDelay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
    const jitter = Math.random() * 1000; // 0-1000msのランダムな遅延を追加
    const delay = baseDelay + jitter;

    if (this.isDebugMode) {
      console.log(`デバッグモード WebSocket再接続スケジュール: ${delay.toFixed(0)}ms後 (試行 ${this.reconnectAttempts + 1}/${MAX_RECONNECT_ATTEMPTS})`);
    }

    this.reconnectTimeout = setTimeout(() => {
      this.reconnectAttempts++;

      // 最新のトークンを取得（コールバックが設定されている場合）
      let token = null;
      if (this.getLatestToken) {
        token = this.getLatestToken();
      }

      if (!token) {
        console.error('WebSocket再接続失敗: 最新のIDトークンを取得できません');
        this.notifyAuthErrorHandlers(null, 'トークン取得失敗');
        return;
      }

      this.connect(token).catch(error => {
        console.error('WebSocket再接続失敗:', error);
      });
    }, delay);
  }

  disconnect() {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }

    if (this.isDebugMode) {
      console.log('デバッグモード WebSocket切断完了');
    }
  }

  onMessage(handler) {
    this.messageHandlers.push(handler);
    return () => {
      this.messageHandlers = this.messageHandlers.filter(h => h !== handler);
    };
  }

  onStatusChange(handler) {
    this.statusHandlers.push(handler);
    return () => {
      this.statusHandlers = this.statusHandlers.filter(h => h !== handler);
    };
  }

  /**
   * 認証エラー発生時のハンドラを登録する。
   * WebSocket close code が認可エラー（4401, 4403等）の場合に呼び出される。
   * @param {(code: number|null, reason: string) => void} handler
   * @returns {() => void} 登録解除関数
   */
  onAuthError(handler) {
    this.authErrorHandlers.push(handler);
    return () => {
      this.authErrorHandlers = this.authErrorHandlers.filter(h => h !== handler);
    };
  }

  notifyMessageHandlers(data) {
    this.messageHandlers.forEach(handler => {
      try {
        handler(data);
      } catch (error) {
        console.error('Message handler error:', error);
      }
    });
  }

  notifyStatusChange(connected) {
    this.statusHandlers.forEach(handler => {
      try {
        handler(connected);
      } catch (error) {
        console.error('Status handler error:', error);
      }
    });
  }

  notifyAuthErrorHandlers(code, reason) {
    this.authErrorHandlers.forEach(handler => {
      try {
        handler(code, reason);
      } catch (error) {
        console.error('Auth error handler error:', error);
      }
    });
  }

  get isConnected() {
    return this.socket && this.socket.readyState === WebSocket.OPEN;
  }
}

const webSocketService = new WebSocketService();
export default webSocketService;